import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Database, BarChart3, LogOut, User, Server, Table, Menu, X, Target, TrendingUp, Users, Shield, MessageSquare, Calendar, Headphones, FileText, Package, Activity } from 'lucide-react';
import Login from './Login';
import HostManagement from './HostManagement';
import OLAPCubeBuilder from './OLAPCubeBuilder';
import DockerLogsViewer from './DockerLogsViewer';
import JobMonitor from './JobMonitor';
import UnifiedDashboard from './HtableDashboard';
import HierarchicalTable from './HierarchicalTable';
import ReportScheduler from './ReportScheduler';
import JourneyDashboard from './JourneyDashboard';
import CampaignDashboard from './CampaignDashboard';
import BillingManagement from './BillingManagement';
import CDPDashboard from './CDPDashboard';
import CDPSessionView from './CDPSessionView';
import ChatbotDesigner from './ChatbotDesigner';
import AgentPortal from './AgentPortal';
import RolePermissionsManager from './RolePermissionsManager';
import UserManagement from './UserManagement';
import ServiceManagement from './ServiceManagement';
import ReportsLibrary from './ReportsLibrary'
import { 
  hasActiveSession 
} from './SessionManager';
import EmbedView from './EmbedView';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const CONTEXT_PATH = process.env.REACT_APP_CONTEXT_PATH || '/cdrf';

const ProtectedRoute = ({ children, adminOnly = false, requiredPermission = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredPermission && user.permissions) {
    const [module, action] = requiredPermission.split(':');
    if (!user.permissions[module]?.[action]) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

const AppLayout = () => {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const logoUrl = process.env.REACT_APP_LOGO_URL || 'logo.png';
  const appName = process.env.REACT_APP_NAME || 'CDR Analytics';

  const tenantLogoUrl = user?.role !== 'admin' && user?.host_tenant_id && logoUrl
    ? logoUrl.replace(/(\.[^.]+)$/, `_${user.host_tenant_id}$1`)
    : null;
  const displayLogoUrl = tenantLogoUrl || logoUrl;
  const displayAppName = appName;

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const hasPermission = (moduleKey, action = 'can_view') => {
    return user?.permissions?.[moduleKey]?.[action] ?? false;
  };

  const getAllNavigationItems = () => {
    const items = [];

    if (hasPermission('dashboard')) {
      items.push({ id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/dashboard', module: 'dashboard' });
    }

    if (hasPermission('cdp')) {
      items.push({ id: 'cdp', label: 'Customer Data Platform', icon: Users, path: '/cdp', module: 'cdp' });
    }

    if (hasPermission('chatbot')) {
      items.push({ id: 'chatbot', label: 'Chatbot Designer', icon: MessageSquare, path: '/chatbot', module: 'chatbot' });
    }

    if (hasPermission('agent_portal')) {
      items.push({ id: 'agent-portal', label: 'Agent Portal', icon: Headphones, path: '/agent', module: 'agent_portal' });
    }

    if (hasPermission('journey')) {
      //items.push({ id: 'journey', label: 'Journey Analytics', icon: TrendingUp, path: '/journey', module: 'journey' });
    }

    if (hasPermission('campaigns')) {
      //items.push({ id: 'campaigns', label: 'Campaigns', icon: Target, path: '/campaigns', module: 'campaigns' });
    }

    if (hasPermission('hierarchy')) {
      items.push({ id: 'hierarchy', label: 'Hierarchical Analysis', icon: Table, path: '/hierarchy', module: 'hierarchy' });
      items.push({ id: 'reports', label: 'Reports', icon: FileText, path: '/reports', module: 'hierarchy' });
    }

    if (hasPermission('olap')) {
      items.push({ id: 'olap', label: 'OLAP Builder', icon: BarChart3, path: '/olap', module: 'olap' });
    }

    if (hasPermission('billing')) {
      items.push({ id: 'service-management', label: 'Service Management', icon: Package, path: '/service-management', module: 'billing' });
      items.push({ id: 'billing', label: 'Billing', icon: Database, path: '/billing', module: 'billing' });
    }

    if (hasPermission('hosts')) {
      items.push({ id: 'hosts', label: 'Host Management', icon: Server, path: '/hosts', module: 'hosts' });
    }

    if (hasPermission('logs')) {
      items.push({ id: 'logs', label: 'Logs', icon: Server, path: '/logs', module: 'logs' });
    }

    if (hasPermission('scheduler')) {
      items.push({ id: 'scheduler', label: 'Scheduler', icon: Calendar, path: '/scheduler', module: 'scheduler' });
    }

    if (hasPermission('users')) {
      items.push({ id: 'user-management', label: 'User Management', icon: Users, path: '/user-management', module: 'users' });
    }

    if (user.role === 'admin') {
      items.push({ id: 'job-monitor', label: 'Job Monitor', icon: Activity, path: '/job-monitor', module: 'job_monitor' });
      items.push({ id: 'permissions', label: 'Role Permissions', icon: Shield, path: '/permissions', module: 'permissions' });
    }

    return items;
  };

  const navigationItems = getAllNavigationItems();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigation = (view, params = {}) => {
    const item = navigationItems.find(nav => nav.id === view);
    if (item) {
      navigate(item.path, { state: params });
    } else if (view === 'cdp-session' && params.sessionId) {
      navigate(`/cdp/session/${params.sessionId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="bg-white shadow-sm border-r w-64 flex flex-col">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              {displayLogoUrl ? (
                <img src={displayLogoUrl} alt="Logo" className="h-6 w-6 object-contain" />
              ) : (
                <BarChart3 className="h-6 w-6 text-blue-600" />
              )}
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{displayAppName}</h1>
              <p className="text-xs text-gray-500">{user?.host_tenant_id}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t">
          <div className="flex items-center space-x-3 px-3 py-2">
            <div className="bg-gray-100 p-2 rounded-full">
              <User className="h-4 w-4 text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.username}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.role}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <Routes>
          <Route path="/dashboard" element={
            <ProtectedRoute requiredPermission="dashboard:can_view">
              <UnifiedDashboard user={user} onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/cdp" element={
            <ProtectedRoute requiredPermission="cdp:can_view">
              <CDPDashboard user={user} onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/cdp/session/:sessionId" element={
            <ProtectedRoute requiredPermission="cdp:can_view">
              <CDPSessionViewWrapper user={user} onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/chatbot" element={
            <ProtectedRoute requiredPermission="chatbot:can_view">
              <ChatbotDesigner user={user} onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/agent" element={
            <ProtectedRoute requiredPermission="agent_portal:can_view">
              <AgentPortal user={user} />
            </ProtectedRoute>
          } />
          <Route path="/journey" element={
            <ProtectedRoute requiredPermission="journey:can_view">
              <JourneyDashboard user={user} onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/campaigns" element={
            <ProtectedRoute requiredPermission="campaigns:can_view">
              <CampaignDashboard user={user} onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/hierarchy" element={
            <ProtectedRoute requiredPermission="hierarchy:can_view">
              <HierarchicalTable user={user} onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requiredPermission="hierarchy:can_view">
              <ReportsLibrary />
            </ProtectedRoute>
          } />
          <Route path="/olap" element={
            <ProtectedRoute requiredPermission="olap:can_view">
              <OLAPCubeBuilder user={user} />
            </ProtectedRoute>
          } />
          <Route path="/service-management" element={
            <ProtectedRoute requiredPermission="billing:can_view">
              <ServiceManagement user={user} section="services" onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute requiredPermission="billing:can_view">
              <ServiceManagement user={user} section="billing" onNavigate={handleNavigation} />
            </ProtectedRoute>
          } />
          <Route path="/hosts" element={
            <ProtectedRoute requiredPermission="hosts:can_view">
              <HostManagement user={user} />
            </ProtectedRoute>
          } />
          <Route path="/logs" element={
            <ProtectedRoute adminOnly requiredPermission="logs:can_view">
              <DockerLogsViewer user={user} />
            </ProtectedRoute>
          } />
          <Route path="/scheduler" element={
            <ProtectedRoute requiredPermission="scheduler:can_view">
              <ReportScheduler user={user} />
            </ProtectedRoute>
          } />
          <Route path="/user-management" element={
            <ProtectedRoute requiredPermission="users:can_view">
              <UserManagement user={user} />
            </ProtectedRoute>
          } />
          <Route path="/job-monitor" element={
            <ProtectedRoute adminOnly>
              <JobMonitor />
            </ProtectedRoute>
          } />
          <Route path="/permissions" element={
            <ProtectedRoute adminOnly>
              <RolePermissionsManager user={user} />
            </ProtectedRoute>
          } />
          <Route path="/" element={
            <Navigate to={
              user?.role === 'host_agent' ? '/agent' :
              user?.role === 'host_customer' ? '/chatbot' :
              '/dashboard'
            } replace />
          } />
        </Routes>
      </div>
    </div>
  );
};

const CDPSessionViewWrapper = ({ user, onNavigate }) => {
  const { sessionId } = useLocation().pathname.match(/\/cdp\/session\/(?<sessionId>[^/]+)/)?.groups || {};
  return <CDPSessionView user={user} sessionId={sessionId} onNavigate={onNavigate} />;
};

const LoginWrapper = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = (userData, token, sessionId) => {
    login(userData, token, sessionId); // Pass sessionId
    const from = location.state?.from?.pathname;
    // Role-based default landing page
    const defaultPath = userData?.role === 'host_agent' ? '/agent' :
                        userData?.role === 'host_customer' ? '/chatbot' :
                        '/dashboard';
    navigate(from || defaultPath, { replace: true });
  };

  return <Login onLogin={handleLogin} />;
};

const App = () => {
  return (
    <Router basename={CONTEXT_PATH}>
      <AuthProvider>
        <Routes>
          <Route path="/embed/*" element={<EmbedView />} />
          <Route path="/login" element={<LoginWrapper />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;